import { CheckRounded } from '@mui/icons-material';
import { Box, Button, Menu, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import { useCurrentModule } from '@hooks/useCurrentModule';
import { POSBOUserInfo } from './pos-bo-user-info';

/**
 * Version component displays the current app version and a dropdown menu with version information.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Determines if the version information should be displayed
 * @returns {JSX.Element|null} The rendered Version component or null if not open
 */
const Version = ({ isOpen }: { isOpen: boolean }) => {
  const { primary } = getColor();

  // State for managing the dropdown menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const currentModule = useCurrentModule();

  /**
   * Handles opening the dropdown menu
   * @param {React.MouseEvent<HTMLElement>} event - The click event
   */
  // const handleClick = (event: React.MouseEvent<HTMLElement>) => {
  //   setAnchorEl(event.currentTarget);
  // };

  /**
   * Handles closing the dropdown menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (isOpen) {
    return (
      <Stack
        direction='column'
        sx={{
          mt: 'auto',
        }}
      >
        {['POS', 'BO'].includes(currentModule) ? <POSBOUserInfo /> : undefined}

        <Stack
          direction='row'
          sx={[
            { zIndex: 1 },
            {
              position: 'relative',
              padding: '0 20.5px 18px 20.5px',
              fontSize: '13px',
              fontWeight: '500',
              letterSpacing: '.46px',
              transition: 'all 0.3s ease',
              gap: '4px',
              mt: 'auto',
            },
          ]}
        >
          <Button
            id='version-btn'
            component={Link}
            to='https://servoitsolutions.ph/support/kb/faq.php?id=2116'
            target='_blank'
            size='small'
            color='primary'
            variant='text'
            sx={{
              p: 0,
              minWidth: 'unset',
              width: 'fit-content',
              justifyContent: 'flex-start',
              ':hover': {
                bgcolor: 'transparent',
              },
              mt: 1,
            }}
          >
            {!isOpen ? 'v' : `v${APP_VERSION}`}
          </Button>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 1,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.025))',
              mt: 0,
              ul: {
                padding: 0,
              },
              borderRadius: '8px',
              width: '240px',
            },
          }}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#F7F7FB',
              borderRadius: '8px',
              padding: '8px 14px',
            }}
          >
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: '500',
                letterSpacing: '.46px',
                color: primary,
              }}
            >
              {`v${APP_VERSION}`}
            </Typography>
            <CheckRounded
              sx={{ color: primary, width: '18px', height: '18px' }}
            />
          </Box>

          <Box sx={{ padding: '10px 12px' }}>
            <Box
              sx={{
                marginLeft: '22px',
                'ul li::marker': {
                  color: '#637381',
                  fontSize: '8px',
                },
                'ul li p': {
                  fontSize: '12px',
                  letterSpacing: '.4px',
                },
                color: '#637381',
                padding: '10px 0',
              }}
            >
              <ul style={{ listStyleType: 'disc' }}>
                <li>
                  <Typography>A major release every 12 months.</Typography>
                </li>
                <li>
                  <Typography>
                    1-3 minor releases for each major release.
                  </Typography>
                </li>
                <li>
                  <Typography>
                    A patch release every week (anytime for an urgent bug fix).
                  </Typography>
                </li>
              </ul>
            </Box>
          </Box>
        </Menu>
      </Stack>
    );
  }

  return null;
};

export default Version;
